<template>
  <div class="section-switcher">
    <button class="switcher-button" @click="toggleSwitcher" :class="{ active: switcherOpen }">
      <div class="switcher-content">
        <div class="section-info">
          <div class="section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <g v-html="getIconPath(sectionConfig[currentSection].icon)"></g>
            </svg>
          </div>
          <span class="current-section">{{ sectionConfig[currentSection].title }}</span>
        </div>
        <svg class="switcher-arrow" :class="{ open: switcherOpen }" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
    </button>

    <Transition name="switcher-dropdown">
      <div class="switcher-dropdown" v-if="switcherOpen">
        <Section
          v-for="section in Object.keys(sectionConfig)"
          :key="section"
          :section="section"
          :current-section="currentSection"
          :title="sectionConfig[section].title"
          :subtitle="sectionConfig[section].subtitle"
          :icon="sectionConfig[section].icon"
          @select="switchSection"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { defaultRoutes } from '@/config/navConfig';
import { featureFlags } from '@/config/featureFlags';
import { getIconPath } from '@/Modules/utils/icons';
import Section from './Section.vue';

const props = defineProps({
  currentSection: String,
  switcherOpen: Boolean,
});

const emit = defineEmits(['update:currentSection', 'update:switcherOpen']);

const router = useRouter();

const allSections = {
  orchestrator: {
    title: 'Orchestrator',
    subtitle: 'Manage workflows',
    icon: 'orchestratorIcon',
  },
  analytics: {
    title: 'Analytics',
    subtitle: 'View insights',
    icon: 'analyticsIcon',
  },
  etl: {
    title: 'ETL',
    subtitle: 'Data processing',
    icon: 'etlIcon',
  },
};

// Hide ETL section when the runtime feature flag is off. The same flag also
// gates `/ETL/*` route registration in `router/index.js`, so deep-links fall
// through to the catch-all redirect.
const sectionConfig = Object.fromEntries(
  Object.entries(allSections).filter(([key]) => key !== 'etl' || featureFlags.etl)
);

const switchSection = (section) => {
  // Update the current section IMMEDIATELY before navigation
  emit('update:currentSection', section);
  emit('update:switcherOpen', false);
  
  // Navigate to the route after updating the section
  router.push(defaultRoutes[section]);
};

const toggleSwitcher = () => {
  emit('update:switcherOpen', !props.switcherOpen);
};
</script>

<style scoped>
.section-switcher {
  position: relative;
  width: 200px;
}

.switcher-button {
  background: white;
  border: 1px solid var(--highlights);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;
  box-shadow: 0 1px 3px rgba(123, 24, 28, 0.1), 0 1px 2px rgba(123, 24, 28, 0.06);
  color: #374151;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.switcher-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--highlights-background);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.switcher-button:hover::before {
  opacity: 1;
}

.switcher-button:hover {
  border-color: var(--highlights);
  box-shadow: 0 4px 6px rgba(123, 24, 28, 0.15), 0 2px 4px rgba(123, 24, 28, 0.1);
}

.switcher-button.active {
  border-color: var(--highlights);
  box-shadow: inset 0 2px 4px rgba(123, 24, 28, 0.1);
  transform: translateY(1px);
}

.switcher-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  position: relative;
  z-index: 1;
  height: 16px;
}

.section-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--highlights);
}

.current-section {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--highlights);
}

.switcher-arrow {
  transition: transform 0.2s ease;
  flex-shrink: 0;
  color: var(--highlights);
}

.switcher-arrow.open {
  transform: rotate(180deg);
}

.switcher-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  padding: 0.5rem;
  z-index: 100;
  overflow: hidden;
}

.switcher-dropdown-enter-active,
.switcher-dropdown-leave-active {
  transition: all 0.2s ease;
}
.switcher-dropdown-enter-from,
.switcher-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}
</style>